import { useState } from 'react';

import { useRescheduleAppointment } from '../hooks/use-appointments';
import { useToastStore } from '../stores/toast-store';

import { CapabilityGate } from './CapabilityGate';

export interface AppointmentRescheduleModalProps {
  appointmentId: string;
  expectedVersion: number;
  open: boolean;
  onClose: () => void;
}

/**
 * AppointmentRescheduleModal — reagenda um appointment (ADR-115) do painel direito.
 *
 * Gated por capability `appointments` (Solo+). `newScheduledAt` capturado via
 * datetime-local e convertido para ISO no submit. O backend cria o sucessor e
 * marca `rescheduledFromId`; a invalidação de `['appointments']` refaz a lista.
 */
export function AppointmentRescheduleModal({
  appointmentId,
  expectedVersion,
  open,
  onClose,
}: AppointmentRescheduleModalProps): JSX.Element | null {
  const reschedule = useRescheduleAppointment();
  const [scheduledAt, setScheduledAt] = useState('');
  const [reason, setReason] = useState('');

  if (!open) return null;

  const handleSubmit = (): void => {
    reschedule.mutate(
      {
        appointmentId,
        expectedVersion,
        body: {
          newScheduledAt: new Date(scheduledAt).toISOString(),
          ...(reason.length > 0 && { reason }),
        },
      },
      {
        onSuccess: () => {
          onClose();
          setScheduledAt('');
          setReason('');
          useToastStore.getState().push({
            variant: 'success',
            title: 'Agendamento reagendado',
            description: 'A nova data foi registrada na conversa.',
            durationMs: 3500,
          });
        },
        onError: (err) => {
          useToastStore.getState().push({
            variant: 'error',
            title: 'Falha ao reagendar',
            description: err instanceof Error ? err.message : 'Erro desconhecido',
            durationMs: 6000,
          });
        },
      },
    );
  };

  const inputStyle = {
    background: 'var(--bg-surface-2)',
    borderColor: 'var(--border-default)',
  };

  return (
    <CapabilityGate capability="appointments">
      <div
        role="dialog"
        aria-label="Reagendar"
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      >
        <div
          className="w-full max-w-lg rounded-lg p-6 shadow-xl"
          style={{ background: 'var(--bg-surface)' }}
        >
          <h2 className="mb-4 text-lg font-semibold">Reagendar</h2>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Nova data e hora</span>
            <input
              type="datetime-local"
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={scheduledAt}
              onChange={(e): void => setScheduledAt(e.target.value)}
              disabled={reschedule.isPending}
            />
          </label>

          <label className="mb-3 block">
            <span className="mb-1 block text-sm font-medium">Motivo (opcional)</span>
            <input
              className="w-full rounded border px-2 py-1"
              style={inputStyle}
              value={reason}
              onChange={(e): void => setReason(e.target.value)}
              disabled={reschedule.isPending}
              maxLength={500}
              placeholder="Ex.: cliente pediu outro horário"
            />
          </label>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border px-4 py-2 text-sm"
              style={{ borderColor: 'var(--border-default)' }}
              onClick={onClose}
              disabled={reschedule.isPending}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={handleSubmit}
              disabled={reschedule.isPending || scheduledAt.length === 0}
            >
              {reschedule.isPending ? 'Reagendando…' : 'Reagendar'}
            </button>
          </div>
        </div>
      </div>
    </CapabilityGate>
  );
}
